import React, { Component } from 'react'
import { findDOMNode } from 'react-dom'
import { translate } from 'react-polyglot'
import anime from 'animejs'
import history from 'configureHistory'

import { Text, Modal, Button, Icon } from 'components'
import EE from 'emitter.js'

import './achievement.scss'

class AchievementModal extends Component {
	constructor(props) {
		super(props)

		this.fireworks = React.createRef()
		this.keyhole = React.createRef()
		this.badge = React.createRef()
		this.lockShackle = React.createRef()
		this.check = React.createRef()

		this.state = {
			achievementsModalOpen: false,
			title: '',
			text: '',
			image: null,
			buttonText: null,
			buttonCallback: null,
			tutorial: false,
			showAll: true,
			colors: [
				'rgba(199, 147, 251, .25)',
				'rgba(199, 147, 251, .5)',
				'rgba(199, 147, 251, .75)',
			],
			numberOfParticules: 48,
			animations: [],
		}
	}

	componentDidMount() {
		window.addEventListener('resize', this.setCanvasSize)

		EE.on('showAchievementsModal', this.showAchievementsModal)
	}

	componentWillUnmount() {
		EE.off('showAchievementsModal')
	}

	setCanvasSize() {
		const { fireworks } = this

		if (!this.fireworks) return

		const canvas = fireworks.current

		if (!canvas) return
		const canvasRect = findDOMNode(canvas).getBoundingClientRect()

		canvas.width = canvasRect.width
		canvas.height = canvasRect.height
	}

	showAchievementsModal = ({
		type,
		image,
		buttonCallback = null,
		tutorial = false,
		showAll = true,
	}) => {
		const { achievementsModalOpen } = this.state
		const { t } = this.props

		if (achievementsModalOpen) return

		const title = t(`achievements.${type}.title`)
		const text = t(`achievements.${type}.text`)

		const buttonText = buttonCallback
			? t(`achievements.${type}.buttonText`)
			: null

		this.setState(
			{
				achievementsModalOpen: true,
				title,
				text,
				image,
				buttonText,
				buttonCallback,
				tutorial,
				showAll,
			},
			() => {
				if (!tutorial) this.animate()
			}
		)
	}

	hideAchievementsModal = () => {
		this.setState({ achievementsModalOpen: false })
	}

	getFontSize = () => {
		const fontSize = parseFloat(
			getComputedStyle(document.documentElement).fontSize
		)
		return fontSize
	}

	animate = () => {
		const lockShackle = this.lockShackle.current
		const badge = this.badge.current
		const keyhole = this.keyhole.current
		const check = this.check.current

		const canvas = this.fireworks.current
		if (!canvas) return

		const canvasWidth = findDOMNode(canvas).getBoundingClientRect().width

		const that = this

		const myCallback = function() {
			badge.classList.add('animation-start')

			anime({
				targets: lockShackle,
				translateY: 0,
				rotate: '90',
				opacity: 0,
				duration: 360,
				delay: 130,
				easing: 'cubicBezier(.52, 0, .74, .74)',
			})
		}

		const showCheckIcon = function() {
			check.classList.add('show')
		}

		anime({
			delay: 0,
			targets: lockShackle,
			translateY: -10,
			duration: 330,
			complete: myCallback,
			easing: 'cubicBezier(.52, 0, .74, 0)',
		})

		anime({
			delay: 750,
			targets: keyhole,
			scale: [1, 17],
			duration: 260,
			easing: 'cubicBezier(.64, 0, .78, .77)',
		})

		anime({
			delay: 660,
			targets: badge,
			scale: [1, 1.7],
			duration: 430,
			easing: 'cubicBezier(.4, 0, .68, .87)',
			complete() {
				showCheckIcon()
				anime({
					targets: badge,
					scale: [1.7, 1],
					duration: 230,
					easing: 'cubicBezier(.52, 0, .74, .14)',
					complete() {
						that.animateParticules(canvasWidth / 2, 80)
					},
				})
			},
		})
	}

	animateParticles = () => {
		const { animations } = this.state
		const el = this.fireworks.current

		anime({
			duration: Infinity,
			update() {
				const ctx = el.getContext('2d')
				ctx.clearRect(0, 0, el.width, el.height)
				animations.forEach(anim => {
					anim.animatables.forEach(animatable => {
						animatable.target.draw()
					})
				})
			},
		})
	}

	animateParticules(x, y) {
		if (!this.fireworks.current) {
			return true
		}

		this.setCanvasSize()

		const particules = this.createParticles(x, y)
		const circle = this.createCircle(x, y)
		const particulesAnimation = anime({
			targets: particules,
			x(p) {
				return p.x + anime.random(-200, 200)
			},
			y(p) {
				return p.y + anime.random(-200, 200)
			},
			radius: 0,
			duration() {
				return anime.random(1200, 1800)
			},
			easing: 'easeOutExpo',
		})

		const circleAnimation = anime({
			targets: circle,
			radius() {
				return anime.random(20 * 7.75, 20 * 10.25)
			},
			lineWidth: 0,
			alpha: {
				value: 0,
				easing: 'linear',
				duration() {
					return anime.random(400, 600)
				},
			},
			duration() {
				return anime.random(1200, 1800)
			},
			easing: 'easeOutExpo',
		})

		this.state.animations.push(particulesAnimation)
		this.state.animations.push(circleAnimation)

		this.animateParticles()
	}

	createParticles(x, y) {
		const particules = []

		for (let i = 0; i < this.state.numberOfParticules; i++) {
			const p = this.createParticule(x, y)
			particules.push(p)
		}

		return particules
	}

	createParticule(x, y) {
		const ctx = this.fireworks.current.getContext('2d')
		const p = {}

		p.x = x
		p.y = y
		p.color = this.state.colors[
			anime.random(0, this.state.colors.length - 1)
		]
		p.radius = anime.random(this.getFontSize(), this.getFontSize() * 2)

		p.draw = function() {
			ctx.beginPath()
			ctx.arc(p.x, p.y, p.radius, 0, 2 * Math.PI, true)
			ctx.fillStyle = p.color
			ctx.fill()
		}

		return p
	}

	createCircle(x, y) {
		const ctx = this.fireworks.current.getContext('2d')
		const p = {}

		p.x = x
		p.y = y
		p.color = this.state.colors[
			anime.random(0, this.state.colors.length - 1)
		]
		p.color = '#FFF'
		p.radius = 0
		p.alpha = 1
		p.lineWidth = 6

		p.draw = function() {
			ctx.globalAlpha = p.alpha
			ctx.beginPath()
			ctx.arc(p.x, p.y, p.radius, 0, 2 * Math.PI, true)
			ctx.lineWidth = p.lineWidth
			ctx.strokeStyle = p.color
			ctx.stroke()
			ctx.globalAlpha = 1
		}

		return p
	}

	onButtonClick = callback => {
		this.hideAchievementsModal()

		callback()
	}

	render() {
		const {
			title,
			text,
			image,
			buttonText,
			buttonCallback,
			tutorial,
			showAll,
			achievementsModalOpen,
		} = this.state

		const { t } = this.props

		return (
			<Modal
				visible={achievementsModalOpen}
				onClose={this.hideAchievementsModal}
			>
				<div className="c-achievement">
					<div
						className="c-achievement__lock-shackle"
						ref={this.lockShackle}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="30"
							height="38"
							viewBox="0 0 30 38"
						>
							<path
								fill="#EAEAEA"
								d="M6,15.1165859 C6,10.0751763 10.0359649,6 15,6 C19.9640351,6 24,10.0751763 24,15.1165859 L24,38 L30,38 L30,15.1165859 C30,6.7743843 23.2907989,0 15,0 C6.70920109,0 0,6.7743843 0,15.1165859 L0,18.1165859 L6,18.1165859 L6,15.1165859 Z"
							/>
						</svg>
					</div>
					<div className="c-achievement__badge-wrap">
						<div className="c-achievement__check" ref={this.check}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="26"
								height="26"
								viewBox="0 0 26 26"
							>
								<g fill="none" fillRule="evenodd">
									<circle
										cx="13"
										cy="13"
										r="12"
										className="u-scheme-color"
									/>
									<path
										fill="#FFF"
										fillRule="nonzero"
										d="M13,26 C20.1797017,26 26,20.1797017 26,13 C26,5.82029825 20.1797017,0 13,0 C5.82029825,0 0,5.82029825 0,13 C0,20.1797017 5.82029825,26 13,26 Z M13,24 C6.92486775,24 2,19.0751322 2,13 C2,6.92486775 6.92486775,2 13,2 C19.0751322,2 24,6.92486775 24,13 C24,19.0751322 19.0751322,24 13,24 Z"
									/>
									<path
										fill="#FFF"
										fillRule="nonzero"
										d="M10.985064,15.3244039 L8.53033009,12.8696699 C8.23743687,12.5767767 7.76256313,12.5767767 7.46966991,12.8696699 C7.1767767,13.1625631 7.1767767,13.6374369 7.46966991,13.9303301 L10.4696699,16.9303301 C10.7683313,17.2289915 11.254598,17.2222391 11.5448513,16.9153999 L18.5448513,9.51539989 C18.8294988,9.21448682 18.816313,8.73979619 18.5153999,8.45514869 C18.2144868,8.17050119 17.7397962,8.18368704 17.4551487,8.48460011 L10.985064,15.3244039 Z"
										className="u-scheme-color-foreground"
									/>
								</g>
							</svg>
						</div>
						<div className="c-achievement__badge" ref={this.badge}>
							<img
								src={image}
								alt=""
								className="c-achievement__icon"
							/>
							<div
								className="c-achievement__keyhole"
								ref={this.keyhole}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="80"
									height="80"
									viewBox="0 0 80 80"
								>
									<g fill="none" fillRule="evenodd">
										<path
											fill="#FFF"
											d="M80,0 L80,80 L0,80 L0,0 L80,0 Z M47.3849117,19.5314489 C44.4868912,17.2692113 40.7232807,16.4669077 37.1659946,17.3530423 C32.2603965,18.47951 28.6053076,22.6251099 28.0677743,27.6722392 C27.5302409,32.7193686 30.2289247,37.5538625 34.7853182,39.706226 L30.4739921,60.017849 C30.3778362,60.5246983 30.5105464,61.0484179 30.836012,61.4465025 C31.1614776,61.8445871 31.6458384,62.0756214 32.1572987,62.0767405 L46.9119962,62.0767405 C47.4096384,62.0749446 47.8820482,61.8554722 48.2069415,61.4751342 C48.5318348,61.0947962 48.6772728,60.5909818 48.6056087,60.0941042 L45.2458662,39.7096922 C49.3779627,37.753191 52.0233529,33.5700259 52.0409283,28.9646357 C51.9977355,25.2679139 50.2829322,21.7936866 47.3849117,19.5314489 Z"
										/>
										<path
											fill="#D6D6D6"
											fillRule="nonzero"
											d="M50.5823499,59.7682994 L50.5885015,59.8081137 C50.7423716,60.8749602 50.4299386,61.9572648 49.7302402,62.7763703 C49.026917,63.5997192 48.0012301,64.0762333 46.9119962,64.0801512 L32.1529152,64.0801512 C31.0409191,64.0777133 29.9894066,63.5761544 29.2849996,62.7145778 C28.5842599,61.8574868 28.2992348,60.7326805 28.5142423,59.6018743 L32.5164867,40.7464041 C28.0529686,37.9600299 25.5057284,32.8111189 26.07563,27.4600703 C26.7034479,21.5652209 30.9764437,16.7187912 36.6817355,15.4090394 C40.8375025,14.3738206 45.2342842,15.3110998 48.6176765,17.952226 C51.9963572,20.5896741 53.9939084,24.6367545 54.0443244,28.9722811 C54.0257936,33.8280193 51.5027302,38.2841471 47.453763,40.7863676 L50.5823499,59.7682994 Z M48.6056087,60.0941042 L45.2458662,39.7096922 C49.3779627,37.753191 52.0233529,33.5700259 52.0409283,28.9646357 C51.9977355,25.2679139 50.2829322,21.7936866 47.3849117,19.5314489 C44.4868912,17.2692113 40.7232807,16.4669077 37.1659946,17.3530423 C32.2603965,18.47951 28.6053076,22.6251099 28.0677743,27.6722392 C27.5302409,32.7193686 30.2289247,37.5538625 34.7853182,39.706226 L30.4739921,60.017849 C30.3778362,60.5246983 30.5105464,61.0484179 30.836012,61.4465025 C31.1614776,61.8445871 31.6458384,62.0756214 32.1572987,62.0767405 L46.9119962,62.0767405 C47.4096384,62.0749446 47.8820482,61.8554722 48.2069415,61.4751342 C48.5318348,61.0947962 48.6772728,60.5909818 48.6056087,60.0941042 Z"
										/>
									</g>
								</svg>
							</div>
						</div>
					</div>
					<div className="c-achievement__text">
						<h4>{title}</h4>
						<Text>{text}</Text>
						{!showAll && tutorial && buttonText && (
							<Text>
								<Button
									onClick={() =>
										this.onButtonClick(buttonCallback)
									}
									text={buttonText}
									layout="text"
									icon={<Icon name="arrow-long-right" />}
								/>
							</Text>
						)}
						{showAll && (
							<Text>
								<Button
									onClick={() =>
										this.onButtonClick(() =>
											history.push({
												hash: '#settings-achievements',
											})
										)
									}
									text={t('show_all_achievements')}
									layout="text"
									icon={<Icon name="arrow-long-right" />}
								/>
							</Text>
						)}
					</div>
					<canvas
						className="c-achievement__fireworks"
						ref={this.fireworks}
					/>
				</div>
			</Modal>
		)
	}
}

export default translate()(AchievementModal)
